import Cookies from 'js-cookie'
import { hasTrackingConsent } from './consent'
import {
  decodeLandingToken,
  LANDING_TOKEN_PARAM,
  LANDING_TOKEN_MAX_AGE,
  landingExperiment,
  landingTokenCandidates,
} from './landing-experiment'
import { accountLandingAssignment } from './account-completion'
import {
  LANDING_CLICK_BATCH,
  LANDING_CLICK_LIMIT,
  normalizeLandingClick,
  normalizeLandingExposure,
} from './landing-clicks'

// A pending opt-out is a control record, retained only until the server has
// stopped attributing this enrollment. It survives an offline reload.
const WITHDRAWALS_KEY = 'pc_landing_withdrawals'
let pendingMemory = []
const CLICKS_KEY = 'pc_landing_clicks:'
const clickMemory = new Map()
const clickRequests = new Map()

function clickState(token) {
  const assignment = decodeLandingToken(token)
  if (!assignment) return null
  const key = CLICKS_KEY + assignment.enrollment_id
  if (clickMemory.has(key)) return { key, ...clickMemory.get(key) }
  let saved
  try {
    saved = JSON.parse(sessionStorage.getItem(key) || 'null')
  } catch {
    /* memory fallback */
  }
  const events = (Array.isArray(saved?.events) ? saved.events : [])
    .map(normalizeLandingClick)
    .filter(Boolean)
    .slice(0, LANDING_CLICK_LIMIT)
  const acknowledged = (
    Array.isArray(saved?.acknowledged) ? saved.acknowledged : []
  ).filter((id) => events.some((event) => event.event_id === id))
  return { key, events, acknowledged }
}

function saveClickState({ key, events, acknowledged }) {
  clickMemory.set(key, { events, acknowledged })
  try {
    sessionStorage.setItem(key, JSON.stringify({ events, acknowledged }))
  } catch {
    /* optional storage */
  }
}

export function clearLandingClicks() {
  clickMemory.clear()
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(CLICKS_KEY))
      .forEach((key) => sessionStorage.removeItem(key))
  } catch {
    /* optional storage */
  }
}

// Include the same bounded, deduplicated snapshot in account creation. A fast
// redirect or in-flight click request cannot strip the final CTA from signup.
export function getLandingClickSnapshot(token = getLandingExperimentToken()) {
  return hasTrackingConsent() ? clickState(token)?.events || [] : []
}

export function recordLandingClick(token, target) {
  if (!hasTrackingConsent()) return false
  const state = clickState(token)
  if (!state || state.events.length >= LANDING_CLICK_LIMIT) return false
  const event = normalizeLandingClick({
    ...target,
    event_id: crypto.randomUUID(),
    occurred_at: Date.now(),
  })
  if (!event) return false
  state.events.push(event)
  saveClickState(state)
  return true
}

export async function flushLandingClicks(token = getLandingExperimentToken()) {
  if (!token || !hasTrackingConsent()) return false
  if (clickRequests.has(token)) return clickRequests.get(token)
  const request = (async () => {
    let state = clickState(token)
    while (state && hasTrackingConsent()) {
      const events = state.events
        .filter((event) => !state.acknowledged.includes(event.event_id))
        .slice(0, LANDING_CLICK_BATCH)
      if (!events.length) return true
      if (!(await sendLandingEvent('click', token, null, { events })))
        return false
      if (!hasTrackingConsent()) return false
      state = clickState(token)
      if (!state) return false
      state.acknowledged = [
        ...new Set([
          ...state.acknowledged,
          ...events.map((event) => event.event_id),
        ]),
      ]
      saveClickState(state)
    }
    return false
  })()
  clickRequests.set(token, request)
  try {
    return await request
  } finally {
    clickRequests.delete(token)
  }
}

function pendingWithdrawals() {
  try {
    const stored = JSON.parse(localStorage.getItem(WITHDRAWALS_KEY) || '[]')
    return [
      ...new Set([...pendingMemory, ...(Array.isArray(stored) ? stored : [])]),
    ].filter((token) => decodeLandingToken(token))
  } catch {
    return pendingMemory
  }
}

function saveWithdrawals(tokens) {
  pendingMemory = tokens
  try {
    if (tokens.length)
      localStorage.setItem(WITHDRAWALS_KEY, JSON.stringify(tokens))
    else localStorage.removeItem(WITHDRAWALS_KEY)
  } catch {
    /* The in-memory queue still retries on reconnect. */
  }
}

export function queueLandingWithdrawal(tokens) {
  saveWithdrawals(
    [...new Set([...pendingWithdrawals(), ...tokens])].filter((token) =>
      decodeLandingToken(token),
    ),
  )
}

export async function flushLandingWithdrawals() {
  let success = true
  for (const token of pendingWithdrawals()) {
    if (await sendLandingEvent('withdraw', token)) {
      saveWithdrawals(
        pendingWithdrawals().filter((pending) => pending !== token),
      )
    } else success = false
  }
  return success
}

export function getLandingExperimentToken() {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return null
  // The email signup attempt carries the originating device's assignment. The
  // server verifies its signature; decoding here only rejects malformed data.
  const fromEmail = new URLSearchParams(window.location.search).get(
    LANDING_TOKEN_PARAM,
  )
  const withdrawn = pendingWithdrawals()
  return (
    landingTokenCandidates((name) => Cookies.get(name), fromEmail).find(
      (token) => decodeLandingToken(token) && !withdrawn.includes(token),
    ) || null
  )
}

export function persistAccountLandingToken(token, result) {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return false
  const assignment = accountLandingAssignment(token, result)
  if (!assignment || pendingWithdrawals().includes(token)) return false
  try {
    // An email link can bring device B's original enrollment to device A.
    // Keep that accepted token after navigation so a later opt-out can revoke
    // the same enrollment used for the account's subsequent purchases.
    Cookies.set(landingExperiment(assignment.experiment_id).cookie, token, {
      path: '/',
      sameSite: 'lax',
      secure: window.location.protocol === 'https:',
      expires: new Date(
        (assignment.assigned_at + LANDING_TOKEN_MAX_AGE) * 1000,
      ),
    })
    return true
  } catch {
    return false
  }
}

export async function sendLandingEvent(action, token, idToken, details = {}) {
  if (
    (!token && !(action === 'withdraw' && idToken)) ||
    (action !== 'withdraw' && !hasTrackingConsent())
  )
    return false
  try {
    const response = await fetch(`/api/landing-experiment/${action}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: idToken } : {}),
      },
      body: JSON.stringify({
        token,
        consent: action !== 'withdraw',
        ...(action === 'click' ? { events: details.events } : {}),
        ...(action === 'exposed'
          ? { click_telemetry_version: 1, ...normalizeLandingExposure(details) }
          : {}),
      }),
      keepalive: true,
      signal: AbortSignal.timeout(10000),
    })
    return response.ok
  } catch {
    return false
  }
}
